/**
 * themeManager.js
 * -----------------------------------------------------------------------------
 * Lógica pura (sin React) para aplicar el tema claro/oscuro a nivel de documento.
 * Se usa tanto desde el ThemeProvider de React como desde el script inline de
 * public/index.html (para evitar el flash de tema claro al cargar — FOUC).
 *
 * Estrategia de modo noche (ver docs/dark-mode.md):
 *  1. Atributo `data-theme="dark"` en <html> → activa los overrides SCSS
 *     definidos bajo `html[data-theme="dark"]` (sistema de variables --xflow-*).
 *  2. Se inyecta <link> al tema dark combinado de Syncfusion (material-dark.css)
 *     AL FINAL del <head>, de modo que sobre-escribe el tema light estático
 *     (misma especificidad → gana el último). Cubre tablas, popups, inputs,
 *     dropdowns, diagramas y navegación de Syncfusion con estilos oficiales.
 *  3. MUI se maneja aparte vía su ThemeProvider (palette.mode).
 *
 * El modo claro queda 100% intacto: si el tema es 'light' no se inyecta nada
 * y no aplica ningún override (todo está scopeado bajo [data-theme="dark"]).
 */

export const THEME_STORAGE_KEY = "xflow-theme";
export const VALID_THEMES = ["light", "dark"];
const SYNCFUSION_DARK_LINK_ID = "xflow-sf-dark-theme";

/** Ruta al tema dark combinado de Syncfusion (copiado a /public/themes). */
function syncfusionDarkHref() {
  // PUBLIC_URL respeta el homepage/base path configurado en CRA.
  const base =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.PUBLIC_URL) ||
    "";
  return `${base}/themes/material-dark.css`;
}

/** Lee la preferencia guardada o, si no hay, la del sistema operativo. */
export function getInitialTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && VALID_THEMES.includes(stored)) return stored;
  } catch (_) {
    /* localStorage puede no estar disponible (modo privado/SSR) */
  }
  try {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch (_) {
    /* matchMedia no disponible */
  }
  return "light";
}

/** Dispara resize para que Syncfusion Grid/List remidan sus celdas. */
function triggerReflow() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("resize"));
  }
}

/** Inyecta o elimina el <link> del tema dark de Syncfusion. */
function applySyncfusionTheme(theme) {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(SYNCFUSION_DARK_LINK_ID);
  if (theme === "dark") {
    if (!existing) {
      const link = document.createElement("link");
      link.id = SYNCFUSION_DARK_LINK_ID;
      link.rel = "stylesheet";
      link.href = syncfusionDarkHref();
      // Después de cargar el CSS, forzar re-medición de componentes Syncfusion.
      link.onload = triggerReflow;
      // appendChild → queda al final del <head> y sobre-escribe el light.
      document.head.appendChild(link);
    }
  } else if (existing) {
    existing.remove();
    // Al quitar el dark CSS, también re-medir (remoción es sincrónica).
    triggerReflow();
  }
}

/**
 * Aplica el tema al documento (atributo + Syncfusion). No persiste ni toca MUI.
 * @param {'light'|'dark'} theme
 */
export function applyTheme(theme) {
  const normalized = VALID_THEMES.includes(theme) ? theme : "light";
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("data-theme", normalized);
    // <meta theme-color> para la barra del navegador en móvil.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", normalized === "dark" ? "#0b1220" : "#ffffff");
    }
  }
  applySyncfusionTheme(normalized);
  return normalized;
}

/** Persiste la preferencia del usuario. */
export function persistTheme(theme) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (_) {
    /* ignore */
  }
}
